import React, { useState, useEffect } from 'react';
import { ModernButton } from '../ui/ModernButton';

// Helper function to convert names to URL-friendly format
const toUrlFriendly = (name) => {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')  // Replace spaces with hyphens
    .replace(/[^a-z0-9-]/g, '')  // Remove any other special characters
    .replace(/-+/g, '-')  // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, '');  // Remove leading/trailing hyphens
};

export function CustomRouteModal({ isOpen, onClose, onConfirm, existingRoutes = [], initialData = null, campaignName = '' }) {
  const [formData, setFormData] = useState({
    companyName: '',
    campaignName: '',
    landingPageName: 'landing'
  });
  const [errors, setErrors] = useState({});
  const [isValidating, setIsValidizing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        // Pre-populate with existing route data
        setFormData({
          companyName: initialData.companyName || 'eiei',
          campaignName: initialData.campaignName || toUrlFriendly(campaignName),
          landingPageName: initialData.landingPageName || 'landing'
        });
      } else {
        // Reset to default values - convert campaign name to URL-friendly format
        setFormData({
          companyName: 'eiei',
          campaignName: toUrlFriendly(campaignName),
          landingPageName: 'landing'
        });
      }
      setErrors({});
    }
  }, [isOpen, initialData, campaignName]);

  const validateForm = () => {
    const newErrors = {};
    
    // Company name validation
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Firmenname ist erforderlich';
    } else if (!/^[a-z0-9-]+$/.test(formData.companyName.trim())) {
      newErrors.companyName = 'Nur Kleinbuchstaben, Zahlen und Bindestriche erlaubt';
    }
    
    // Campaign name validation
    if (!formData.campaignName.trim()) {
      newErrors.campaignName = 'Kampagnenname ist erforderlich';
    } else if (!/^[a-z0-9-]+$/.test(formData.campaignName.trim())) {
      newErrors.campaignName = 'Nur Kleinbuchstaben, Zahlen und Bindestriche erlaubt';
    }
    
    // Landing page name validation
    if (!formData.landingPageName.trim()) {
      newErrors.landingPageName = 'Landing Page Name ist erforderlich';
    } else if (!/^[a-z0-9-]+$/.test(formData.landingPageName.trim())) {
      newErrors.landingPageName = 'Nur Kleinbuchstaben, Zahlen und Bindestriche erlaubt';
    }
    
    // Check for duplicates
    const fullRoute = `/${formData.companyName.trim()}/${formData.campaignName.trim()}/${formData.landingPageName.trim()}`;
    if (existingRoutes.includes(fullRoute)) {
      newErrors.duplicate = 'Diese Route existiert bereits';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    // Convert to lowercase and only replace spaces with hyphens, keep hyphens and underscores
    const sanitizedValue = value.toLowerCase().replace(/\s+/g, '-');
    
    setFormData(prev => ({
      ...prev,
      [field]: sanitizedValue
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsValidizing(true);
    const isValid = validateForm();
    setIsValidizing(false);
    
    if (isValid) {
      onConfirm({
        companyName: formData.companyName.trim(),
        campaignName: formData.campaignName.trim(),
        landingPageName: formData.landingPageName.trim()
      });
    }
  };

  const generatePreviewUrl = () => {
    if (formData.companyName && formData.campaignName && formData.landingPageName) {
      return `/${formData.companyName}/${formData.campaignName}/${formData.landingPageName}`;
    }
    return '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
              {initialData ? 'Individuelle URL Route bearbeiten' : 'Individuelle URL Route erstellen'}
            </h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Firmenname *
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                placeholder="z.B. eiei"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.companyName ? 'border-red-300' : 'border-slate-300 dark:border-slate-600'
                } bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200`}
              />
              {errors.companyName && (
                <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Kampagnenname *
              </label>
              <input
                type="text"
                value={formData.campaignName}
                onChange={(e) => handleInputChange('campaignName', e.target.value)}
                placeholder="z.B. software-engineer, gamedev, jobs2024"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.campaignName ? 'border-red-300' : 'border-slate-300 dark:border-slate-600'
                } bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200`}
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Kann völlig anders sein als der Kampagnenname. Wird automatisch aus "{campaignName}" vorgeschlagen.
              </p>
              {errors.campaignName && (
                <p className="mt-1 text-sm text-red-600">{errors.campaignName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Landing Page Name *
              </label>
              <input
                type="text"
                value={formData.landingPageName}
                onChange={(e) => handleInputChange('landingPageName', e.target.value)}
                placeholder="z.B. landing, jobs, careers"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.landingPageName ? 'border-red-300' : 'border-slate-300 dark:border-slate-600'
                } bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200`}
              />
              {errors.landingPageName && (
                <p className="mt-1 text-sm text-red-600">{errors.landingPageName}</p>
              )}
            </div>

            {generatePreviewUrl() && (
              <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">Vorschau URL:</p>
                <p className="text-sm font-mono text-slate-800 dark:text-slate-200">
                  https://karrieresichern.de{generatePreviewUrl()}
                </p>
              </div>
            )}

            {errors.duplicate && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{errors.duplicate}</p>
              </div>
            )}

            <div className="flex items-center justify-end space-x-3 pt-4">
              <ModernButton
                type="button"
                onClick={onClose}
                variant="secondary"
                disabled={isValidating}
              >
                Abbrechen
              </ModernButton>
              <ModernButton
                type="submit"
                variant="primary"
                disabled={isValidating}
              >
                {isValidating ? 'Validiere...' : 'Route erstellen'}
              </ModernButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
